/**
 * One picker per slot of a report kind: a ready object of the slot's kind, or nothing for an
 * optional slot. Pure: the caller holds the selection and decides when it is applied.
 */
import { ActionIcon, Group, Select, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { DataObject } from '@/api/objects';
import type { ReportInput, ReportSlot } from '@/api/reports';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';

/** The project's objects a slot may be bound to: its kind, ready, not deleted. */
export function eligibleFor(objects: DataObject[], slot: ReportSlot): DataObject[] {
  return objects.filter((o) => o.kind === slot.kind && o.client_status === 'ready' && !o.deleted);
}

/** The selection a report's current inputs amount to, by role. */
export function selectionOf(inputs: ReportInput[]): Record<string, string | null> {
  return Object.fromEntries(inputs.map((i) => [i.role, i.object.public_id]));
}

export interface ReportInputsFieldsProps {
  slots: ReportSlot[];
  objects: DataObject[];
  /** What the report is bound to now, so a bound object stays selectable even when no longer eligible. */
  current: ReportInput[];
  value: Record<string, string | null>;
  onChange: (role: string, object: string | null) => void;
  disabled?: boolean;
}

export function ReportInputsFields({ slots, objects, current, value, onChange, disabled = false }: ReportInputsFieldsProps) {
  const { t } = useTranslation('reports');
  const kindLabel = useKindLabel();
  if (!slots.length) return null;
  const bound = new Map(current.map((i) => [i.role, i.object]));
  return (
    <Stack gap="sm">
      <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }}>
        {t('table.inputs')}
      </Text>
      {slots.map((slot) => {
        const selected = value[slot.role] ?? null;
        const choices = eligibleFor(objects, slot);
        const data = choices.map((o) => ({ value: o.public_id, label: objectName(o) }));
        const was = bound.get(slot.role);
        if (was && !choices.some((o) => o.public_id === was.public_id)) data.unshift({ value: was.public_id, label: objectName(was) });
        const label = `${t(`roles.${slot.role}`, { defaultValue: slot.role })}${slot.required ? '' : ` (${t('create.optional')})`}`;
        return (
          <Group key={slot.role} gap="xs" align="flex-end" wrap="nowrap">
            <Select
              style={{ flex: 1 }}
              label={label}
              description={kindLabel(slot.kind)}
              placeholder={data.length ? t('create.pick') : t('create.none')}
              disabled={disabled || !data.length}
              data={data}
              value={selected}
              allowDeselect={false}
              onChange={(v) => v && v !== selected && onChange(slot.role, v)}
            />
            {!slot.required && selected && (
              <ActionIcon variant="default" size="lg" aria-label={t('inputs.unbind')} title={t('inputs.unbind')} disabled={disabled} onClick={() => onChange(slot.role, null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </ActionIcon>
            )}
          </Group>
        );
      })}
      <Text fz={12} c={brand.textMuted}>
        {t('inputs.hint')}
      </Text>
    </Stack>
  );
}
